import { defineStore } from 'pinia';
import { fetchAudiences, createAudience, updateAudience, fetchSuspects } from '@/services/ppApi';
import { useAccountStore } from '@/stores/account';
import { useApiCreditsStore } from '@/stores/apiCredits';

export const useAudienceStore = defineStore('audience', {
    state: () => {
        return {
            loading: false,
            loadingSave: false,
            error: null,
            audience: null
        }
    },
    actions: {
        resetAudience() {
            this.$reset();
        },
        async fetchAudience(id, router = null, stats = false) {
            this.loading = true;
            this.error = null;

            try {
                const accountStore = useAccountStore();
                const account_id = accountStore.account.account_id;

                const response = await fetchAudiences({
                    audience_id: id,
                    account_id,
                    ...(stats ? { add_stats: stats === true ? 1 : stats } : {}),
                });

                if(response?.data?.found && response.data.found > 0 && response.data.audiences[0].audience_id === id) {
                    this.audience = response.data.audiences[0];
                    return true;
                } else {
                    this.resetAudience();
                    
                    if(router) {
                        router.push({ name: 'audiences'});
                    }
                    return false;
                }
            } catch(error) {
                this.error = error;
                return false;
            } finally {
                this.loading = false;
            }
        },
        async fetchStats(id = null) {
            if(!id) {
                id = this.audience?.audience_id;
            }
            return await this.fetchAudience(id, null, 10000);
        },
        async getAudience(id, router = null, stats = false) {
            if (!this.loading) {
                if (!this.audience?.audience_id || this.audience.audience_id !== id) {
                    await this.fetchAudience(id, router, stats);
                } else if (stats && this.audience?.audience_id === id && !this.audience?.stats) {
                    await this.fetchAudience(id, router, stats);
                }
            }
            return this.audience || false;
        },
        async createAudience(audience) {
            this.loadingSave = true;
            this.error = null;

            try {
                const accountStore = useAccountStore();
                const account_id = accountStore.account.account_id;

                const payload = {
                    ...audience,
                    account_id,
                    add_stats: 1
                };

                const response = await createAudience(payload);

                if(response?.data?.audiences?.length > 0) {
                    this.audience = response.data.audiences[0];
                    return true;
                } else {
                    return false;
                }
            } catch(error) {
                this.error = error;
                return false;
            } finally {
                this.loadingSave = false;
            }
        },
        async updateAudience(audience) {
            this.loadingSave = true;
            this.error = null;

            try {
                const accountStore = useAccountStore();
                const account_id = accountStore.account.account_id;

                const payload = {
                    ...audience,
                    account_id,
                    add_stats: 1
                };

                const response = await updateAudience(payload);

                if(response?.data?.audiences?.length > 0) {
                    this.audience = response.data.audiences[0];
                    return true;
                } else {
                    return false;
                }
            } catch(error) {
                this.error = error;
                return false;
            } finally {
                this.loadingSave = false;
            }
        },
        async addProspectsInBulk(count) {
            try {
                const accountStore = useAccountStore();
                const account_id = accountStore.account.account_id;

                const payload = {
                    account_id,
                    add_batch: count,
                    qid: this.audience.qid,
                    ...this.audience.query
                };

                const response = await fetchSuspects(payload);

                if(response?.data?.credits_used > 1) {
                    const apiCreditsStore = useApiCreditsStore();
                    const newCredits = {
                        ...(response.data.monthly_credits ? {monthly_credits: response.data.monthly_credits} : {}),
                        ...(response.data.credits_used_month ? {credits_used_month: response.data.credits_used_month} : {}),
                        ...(response.data.monthly_perc ? {monthly_perc: response.data.monthly_perc} : {}),
                    };
                    apiCreditsStore.updatePpCredits(newCredits);
                }

                if(response?.data?.status === 'ok') {
                    return true;
                } else {
                    return false;
                }
            } catch(error) {
                return error?.response?.data?.error_code || false;
            }
        }
    }
});